import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, Plus, Check, X, Coffee } from 'lucide-react';

/**
 * Компонент для планирования международных встреч с учетом часовых поясов участников
 */
const MeetingPlanner = ({ selectedCities, onAddCity }) => {
  const [participants, setParticipants] = useState([
    { name: 'Москва', timezone: 'Europe/Moscow', workHoursStart: 9, workHoursEnd: 18 },
    { name: 'Нью-Йорк', timezone: 'America/New_York', workHoursStart: 9, workHoursEnd: 17 }
  ]);
  const [meetingDate, setMeetingDate] = useState(new Date());
  const [meetingLength, setMeetingLength] = useState(60); // в минутах
  const [bestTimes, setBestTimes] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [availableTimezones, setAvailableTimezones] = useState([]);

  // Заполняем список доступных часовых поясов из selectedCities
  useEffect(() => {
    if (selectedCities && selectedCities.length > 0) {
      const cities = selectedCities.map(city => ({
        name: city.name,
        timezone: city.timezone
      }));
      setAvailableTimezones(cities);
    }
  }, [selectedCities]);

  // Функция добавления участника
  const addParticipant = (city) => {
    if (!participants.some(p => p.name === city.name)) {
      setParticipants([
        ...participants, 
        { 
          name: city.name, 
          timezone: city.timezone, 
          workHoursStart: 9, 
          workHoursEnd: 18 
        }
      ]);
    }
    setShowAddParticipant(false);
  };

  // Функция удаления участника
  const removeParticipant = (name) => {
    if (participants.length <= 2) {
      alert('Для планирования необходимо минимум 2 участника');
      return;
    }
    setParticipants(participants.filter(p => p.name !== name));
  };

  // Функция обновления рабочих часов участника
  const updateParticipantWorkHours = (name, field, value) => {
    setParticipants(participants.map(p => {
      if (p.name === name) {
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  // Функция нахождения оптимального времени для встречи
  const findBestMeetingTimes = () => {
    const meetingDurationHours = meetingLength / 60;
    const allPossibleTimes = [];
    const meetingDay = new Date(meetingDate);
    
    // Перебираем каждый час дня для поиска подходящего времени
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startTime = new Date(meetingDay);
        startTime.setHours(hour, minute, 0, 0);
        
        const endTime = new Date(startTime);
        endTime.setTime(startTime.getTime() + meetingLength * 60 * 1000);
        
        // Проверяем, подходит ли это время для всех участников
        const suitableForAll = participants.every(participant => {
          // Конвертируем время встречи в локальное время участника
          const participantStartTime = getTimeInTimezone(startTime, participant.timezone);
          const participantEndTime = getTimeInTimezone(endTime, participant.timezone);
          
          // Проверяем, попадают ли начало и конец встречи в рабочие часы участника
          const startHour = participantStartTime.getHours();
          const endHour = participantEndTime.getHours();
          const endMinute = participantEndTime.getMinutes();
          
          return (startHour >= participant.workHoursStart && 
                 (endHour < participant.workHoursEnd || 
                  (endHour === participant.workHoursEnd && endMinute === 0)));
        });
        
        if (suitableForAll) {
          allPossibleTimes.push({
            startTime,
            endTime,
            score: calculateTimeScore(startTime, participants)
          });
        }
      }
    }
    
    // Сортируем времена по оценке (лучшие сначала)
    allPossibleTimes.sort((a, b) => b.score - a.score);
    
    // Берем первые 5 лучших вариантов
    setBestTimes(allPossibleTimes.slice(0, 5));
  };

  // Конвертация времени в указанный часовой пояс
  const getTimeInTimezone = (date, timezone) => {
    try {
      // Получаем текущее время в UTC
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      
      // Конвертируем UTC время в указанный часовой пояс
      const tzDate = new Date(utcDate.toLocaleString('en-US', { timeZone: timezone }));
      
      // Учитываем разницу из-за часового пояса
      const offset = date.getTime() - tzDate.getTime();
      const newDate = new Date(date.getTime() + offset);
      
      return newDate;
    } catch (error) {
      console.error('Ошибка конвертации времени:', error);
      return date; // Возвращаем исходную дату в случае ошибки
    }
  };

  // Расчет оценки удобства времени (по удаленности от краев рабочего дня)
  const calculateTimeScore = (startTime, participants) => {
    let totalScore = 0;
    
    participants.forEach(participant => {
      const localTime = getTimeInTimezone(startTime, participant.timezone);
      const hour = localTime.getHours();
      
      // Рассчитываем, насколько время удалено от краев рабочего дня
      const distanceFromStart = hour - participant.workHoursStart;
      const distanceFromEnd = participant.workHoursEnd - hour;
      
      // Идеальное время - середина рабочего дня
      const workDayMiddle = (participant.workHoursStart + participant.workHoursEnd) / 2;
      const distanceFromMiddle = Math.abs(hour - workDayMiddle);
      
      // Чем ближе к середине рабочего дня, тем выше оценка
      const score = 10 - distanceFromMiddle;
      
      totalScore += score;
    });
    
    return totalScore;
  };

  // Форматирование времени
  const formatTime = (date, timezone) => {
    try {
      return date.toLocaleTimeString('ru-RU', { 
        timeZone: timezone,
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Ошибка форматирования времени:', error);
      return '--:--';
    }
  };

  // Обработчик выбора времени встречи
  const handleSelectTime = (time) => {
    setSelectedTime(time);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center">
        <Users className="w-5 h-5 mr-2 text-indigo-500" />
        Планировщик международных встреч
      </h2>

      {/* Параметры встречи */}
      <div className="mb-6 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 border border-indigo-100 dark:border-indigo-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Параметры встречи</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Дата встречи
            </label>
            <input
              type="date"
              value={meetingDate.toISOString().substr(0, 10)}
              onChange={(e) => setMeetingDate(new Date(e.target.value))}
              className="block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Продолжительность (минуты)
            </label>
            <select
              value={meetingLength}
              onChange={(e) => setMeetingLength(parseInt(e.target.value, 10))}
              className="block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 dark:text-white"
            >
              <option value={30}>30 минут</option>
              <option value={60}>1 час</option>
              <option value={90}>1 час 30 минут</option>
              <option value={120}>2 часа</option>
              <option value={180}>3 часа</option>
            </select>
          </div>
        </div>
      </div>

      {/* Список участников */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Участники встречи</h3>
          
          <button
            type="button"
            onClick={() => setShowAddParticipant(true)}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-1" />
            Добавить участника
          </button>
        </div>

        {/* Выбор нового участника */}
        {showAddParticipant && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <h4 className="text-sm font-medium text-gray-800 dark:text-white mb-2">Выберите город:</h4>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
              {availableTimezones.map((city, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => addParticipant(city)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-white text-sm rounded-md border border-gray-300 dark:border-gray-600 transition-colors flex items-center justify-between"
                >
                  <span>{city.name}</span>
                  <Plus className="w-4 h-4 text-indigo-500" />
                </button>
              ))}
            </div>
            
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={() => setShowAddParticipant(false)}
                className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white text-sm font-medium rounded-md transition-colors"
              >
                Отмена
              </button>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Город</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Часовой пояс</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Рабочие часы</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {participants.map((participant, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{participant.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500 dark:text-gray-400">{participant.timezone.replace('_', ' ')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <select
                        value={participant.workHoursStart}
                        onChange={(e) => updateParticipantWorkHours(participant.name, 'workHoursStart', parseInt(e.target.value, 10))}
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mr-2"
                      >
                        {Array.from({ length: 24 }, (_, i) => i).map(hour => (
                          <option key={hour} value={hour}>{hour}:00</option>
                        ))}
                      </select>
                      <span className="text-gray-500 dark:text-gray-400 mx-1">—</span>
                      <select
                        value={participant.workHoursEnd}
                        onChange={(e) => updateParticipantWorkHours(participant.name, 'workHoursEnd', parseInt(e.target.value, 10))}
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        {Array.from({ length: 24 }, (_, i) => i + 1).map(hour => (
                          <option key={hour} value={hour}>{hour}:00</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      type="button"
                      onClick={() => removeParticipant(participant.name)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Кнопка поиска оптимального времени */}
      <div className="mb-6 flex justify-center">
        <button
          type="button"
          onClick={findBestMeetingTimes}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors flex items-center"
        >
          <Calendar className="w-5 h-5 mr-2" />
          Найти оптимальное время
        </button>
      </div>

      {/* Результаты подбора времени */}
      {bestTimes.length > 0 && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-100 dark:border-green-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Оптимальное время для встречи</h3>
          
          <div className="space-y-3">
            {bestTimes.map((time, index) => (
              <div 
                key={index}
                className={`p-3 rounded-lg border ${
                  selectedTime === time 
                    ? 'bg-green-100 dark:bg-green-800/40 border-green-300 dark:border-green-700' 
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                } hover:bg-green-50 dark:hover:bg-green-900/30 cursor-pointer transition-colors`}
                onClick={() => handleSelectTime(time)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Clock className={`w-5 h-5 mr-2 ${
                      selectedTime === time 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-indigo-500'
                    }`} />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{formatTime(time.startTime, 'UTC')} - {formatTime(time.endTime, 'UTC')} (UTC)</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Продолжительность: {meetingLength} мин.
                      </div>
                    </div>
                  </div>
                  {selectedTime === time && (
                    <div className="ml-4">
                      <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                  )}
                </div>
                
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {participants.map((participant, idx) => (
                    <div 
                      key={idx} 
                      className="text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                    >
                      <div className="font-medium text-gray-800 dark:text-white">{participant.name}</div>
                      <div className="text-indigo-600 dark:text-indigo-400 mt-1">
                        {formatTime(time.startTime, participant.timezone)} - {formatTime(time.endTime, participant.timezone)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Выбранное время встречи */}
      {selectedTime && (
        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <Coffee className="w-5 h-5 mr-2 text-indigo-500" />
            Подробности о встрече
          </h3>
          
          <div className="mb-3">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Дата: {meetingDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Время: {formatTime(selectedTime.startTime, 'UTC')} - {formatTime(selectedTime.endTime, 'UTC')} (UTC)
            </div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Продолжительность: {meetingLength} минут
            </div>
          </div>
          
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Локальное время для участников:
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {participants.map((participant, idx) => (
              <div 
                key={idx} 
                className="p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"
              >
                <div className="font-medium text-gray-800 dark:text-white">{participant.name}</div>
                <div className="text-indigo-600 dark:text-indigo-400 mt-1">
                  {formatTime(selectedTime.startTime, participant.timezone)} - {formatTime(selectedTime.endTime, participant.timezone)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingPlanner;